import { useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import { mutate } from 'swr';
import { storeService } from 'services';
import { useAuth } from 'hooks';
import { FormOption, OpeningDay } from 'types';
import { Button, Checkbox, Heading, Text } from 'components';
import { formatMillisecondsToHours, messages } from 'helpers';
import {
  FontSize,
  FontWeight,
  ONE_MINUTE_MILLISECONDS,
  TEST_IDS,
  weekdayName,
} from 'commons';
import * as S from './styles';
import { HourSelect } from '../hour-select';
import { OpeningDayHistory } from '../opening-day-history';

interface Props {
  openingDays: OpeningDay[];
}

interface FormProps {
  openingDaysValues: {
    weekday: string;
    isClosed: boolean;
    openingTime: number;
    closingTime: number;
  }[];
}

const THIRTY_MINUTES_MILLISECONDS = 30 * ONE_MINUTE_MILLISECONDS;
const MINIMUM_OPENING_TIME = 28800000;
const MAXIMUM_CLOSING_TIME = 81000000;

export function OpeningDays({ openingDays }: Props) {
  const { selectedStore } = useAuth();
  const { name: store } = selectedStore;

  const horizontalScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { current } = horizontalScrollRef;

    if (!current) return;

    function sideScroll(event: any) {
      event.preventDefault();

      current?.scrollBy({ left: event.deltaY / 3, behavior: 'smooth' });
    }

    current?.addEventListener('wheel', sideScroll);

    return () => current?.removeEventListener('wheel', sideScroll);
  }, [horizontalScrollRef]);

  const formHours = useFormik<FormProps>({
    initialValues: (() => {
      const openingDaysValues = openingDays.map((openingDay) => {
        const { weekday, openingTime, closingTime, open } = openingDay;

        return {
          weekday,
          isClosed: !open,
          openingTime: openingTime || MINIMUM_OPENING_TIME,
          closingTime: closingTime || MAXIMUM_CLOSING_TIME,
        };
      });

      return { openingDaysValues };
    })(),

    onSubmit: async (values: any) => {
      if (!store) {
        return messages([{ errorCode: 'FRONT-RE-STORE-NOT-FOUND' }]);
      }

      const dateStartGreaterThanEnd = {
        openingTime: 0,
        closingTime: 0,
      };

      const updatedOpeningSchedule = values.openingDaysValues.map(
        (openingDay: any, index: number) => {
          const { weekday, openingTime, closingTime, isClosed } = openingDay;

          const numOpeningTime = Number(openingTime);
          const numClosingTime = Number(closingTime);

          if (!isClosed && numOpeningTime > numClosingTime) {
            dateStartGreaterThanEnd.openingTime = numOpeningTime;
            dateStartGreaterThanEnd.closingTime = numClosingTime;

            formHours.setFieldError(
              `openingDaysValues[${index}].openingTime`,
              'error'
            );
            formHours.setFieldError(
              `openingDaysValues[${index}].closingTime`,
              'error'
            );
          }

          return {
            weekday,
            isOpen: !isClosed,
            openingTime: isClosed ? undefined : numOpeningTime,
            closingTime: isClosed ? undefined : numClosingTime,
          };
        }
      );

      const { openingTime, closingTime } = dateStartGreaterThanEnd;

      if (openingTime > closingTime) {
        const { hours: openingHours, minutes: openingMinutes } =
          formatMillisecondsToHours(openingTime);
        const { hours: closingHours, minutes: closingMinutes } =
          formatMillisecondsToHours(closingTime);

        return messages([
          {
            errorCode: 'FRONT-RE-CLOSING-TIME-GREATER-THAN-OPEN-TIME',
          },
          {
            subject: `${closingHours}:${closingMinutes}`,
            subjectType: 'closingTime',
          },
          {
            subject: `${openingHours}:${openingMinutes}`,
            subjectType: 'openingTime',
          },
        ]);
      }

      const response = await storeService.postUpdateOpeningSchedule({
        store,
        updatedOpeningSchedule,
      });

      if (response?.status === 200) {
        messages([{ errorCode: 'FRONT-RE-NEW-OPENING-DAYS-UPDATED' }]);

        await mutate(`opening-day-history-${store}`);
        mutate(`profile-${store}`);

        formHours.resetForm({ values });
      }
    },
  });

  if (!openingDays.length) {
    return null;
  }

  const hourOptions: FormOption[] = [];

  for (
    let milliseconds = MINIMUM_OPENING_TIME;
    milliseconds <= MAXIMUM_CLOSING_TIME;
    milliseconds += THIRTY_MINUTES_MILLISECONDS
  ) {
    const { hours, minutes } = formatMillisecondsToHours(milliseconds);

    hourOptions.push({
      label: `${hours}:${minutes}`,
      value: String(milliseconds),
    });
  }

  const renderOptions = hourOptions.map((option) => {
    const { label, value } = option;

    return (
      <option key={value} value={value}>
        {label}
      </option>
    );
  });

  const {
    values,
    isSubmitting,
    dirty,
    handleChange,
    handleSubmit,
    handleReset,
    errors,
  } = formHours;

  const renderDays = openingDays.map((_, index) => {
    const { isClosed, openingTime, closingTime, weekday } =
      values.openingDaysValues[index];
    const displayWeekday = weekdayName[weekday.toLowerCase()]?.full;

    return (
      <S.Container
        $danger={isClosed}
        key={values.openingDaysValues[index].weekday}
      >
        <Text size={FontSize.font12} weight={FontWeight.bold}>
          {displayWeekday}
        </Text>

        <S.TimesWrapper>
          <S.TextAndTimeWrapper>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              de
            </Text>

            <HourSelect
              name={`openingDaysValues[${index}].openingTime`}
              value={openingTime}
              onChange={handleChange}
              disabled={isClosed}
              hasError={Boolean(errors?.openingDaysValues?.[index])}
            >
              {renderOptions}
            </HourSelect>
          </S.TextAndTimeWrapper>

          <S.TextAndTimeWrapper>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              até
            </Text>

            <HourSelect
              name={`openingDaysValues[${index}].closingTime`}
              value={closingTime}
              onChange={handleChange}
              disabled={isClosed}
              hasError={Boolean(errors?.openingDaysValues?.[index])}
            >
              {renderOptions}
            </HourSelect>
          </S.TextAndTimeWrapper>
        </S.TimesWrapper>

        <S.CheckboxWrapper>
          <Checkbox
            name={`openingDaysValues[${index}].isClosed`}
            id={`openingDaysValues[${index}].isClosed`}
            onChange={handleChange}
            checked={isClosed}
            variant="danger"
          />

          <S.Label htmlFor={`openingDaysValues[${index}].isClosed`}>
            Fechado
          </S.Label>
        </S.CheckboxWrapper>
      </S.Container>
    );
  });

  return (
    <S.Main onSubmit={handleSubmit} onReset={handleReset}>
      <S.TitleWrapper>
        <Heading size={FontSize.font16} weight={FontWeight.bold}>
          Horários de atendimento
        </Heading>

        <OpeningDayHistory />
      </S.TitleWrapper>

      <S.Wrapper
        data-testid={TEST_IDS.OPENING_DAYS_DIV}
        ref={horizontalScrollRef}
      >
        {renderDays}
      </S.Wrapper>

      <S.ButtonsWrapper>
        <Button
          data-testid={TEST_IDS.CANCEL_OPENING_DAYS_CHANGES_BUTTON}
          variant="outline-secondary"
          hidden={!dirty}
          type="reset"
        >
          Cancelar
        </Button>

        <Button
          data-testid={TEST_IDS.CONFIRM_OPENING_DAYS_CHANGES_BUTTON}
          disabled={isSubmitting || !dirty}
          type="submit"
        >
          Confirmar alteração
        </Button>
      </S.ButtonsWrapper>
    </S.Main>
  );
}
